import React, { useState, useEffect } from 'react';
import { adminApi, auth } from '@/lib/api';
import { toast } from 'sonner';
import { Building2, ArrowDownToLine, ArrowUpFromLine, Send, DollarSign, Wallet, Edit2, Download, Archive } from 'lucide-react';

export default function BankAccountPage() {
  const [bankData, setBankData] = useState({ balances: {}, stats: {}, revenueTrend: [] });
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [depositForm, setDepositForm] = useState({ amount: '', currency: 'LBP', note: '' });
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', currency: 'LBP', note: '' });
  const [transferForm, setTransferForm] = useState({ userId: '', amount: '', currency: 'LBP', note: '' });

  const [adjustModal, setAdjustModal] = useState({ isOpen: false, balanceType: 'USD', newAmount: '', reason: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [bankRes, txRes, usersRes] = await Promise.all([
        adminApi.get('/admin/bank-account'),
        adminApi.get('/admin/bank-account/transactions'),
        adminApi.get('/public/users')
      ]);
      const raw = bankRes.data || {};
      const revenue = raw.stats || {};
      setBankData({
        balances: { USD: Number(raw.balance_usd || 0), LBP: Number(raw.balance_lbp || 0) },
        stats: {
          todayRevenue: Number(revenue.today_usd || 0), monthlyRevenue: Number(revenue.month_usd || 0),
          subscriptionsRevenue: Number(revenue.by_category?.subscription?.usd || 0),
          marketplaceRevenue: Number(revenue.by_category?.marketplace?.usd || 0),
          totalRevenue: Number(revenue.year_usd || 0)
        },
        revenueTrend: Array.isArray(revenue.daily_usd) ? revenue.daily_usd : []
      });
      setTransactions(Array.isArray(txRes.data?.items) ? txRes.data.items : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (error) {
      setError(error?.response?.data?.detail || 'تعذر تحميل بيانات حساب البنك');
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post('/admin/bank-account/deposit', depositForm);
      toast.success('تم الإيداع بنجاح');
      setDepositForm({ amount: '', currency: 'LBP', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'حدث خطأ أثناء الإيداع');
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post('/admin/bank-account/withdraw', withdrawForm);
      toast.success('تم السحب بنجاح');
      setWithdrawForm({ amount: '', currency: 'LBP', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'حدث خطأ أثناء السحب');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post('/admin/bank-account/transfer-to-user', { ...transferForm, user_id: transferForm.userId });
      toast.success('تم التحويل بنجاح');
      setTransferForm({ userId: '', amount: '', currency: 'LBP', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'حدث خطأ أثناء التحويل');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = auth.getAdmin();
      const response = await fetch('/api/admin/bank-account/export-revenue.csv', { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'family-bank-revenue.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('تعذر تصدير ملف CSV');
    }
  };

  const handleArchive = async (id) => {
    try {
      await adminApi.post(`/admin/bank-account/transactions/${id}/archive`);
      toast.success('تمت أرشفة الحركة بنجاح');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'تعذر أرشفة الحركة');
    }
  };

  const handleManualAdjust = async (e) => {
    e.preventDefault();
    if (!adjustModal.reason.trim()) {
      toast.error('يجب كتابة سبب التعديل');
      return;
    }
    try {
      await adminApi.post('/admin/bank-account/adjust-balance', { currency: adjustModal.balanceType, new_balance: Number(adjustModal.newAmount), reason: adjustModal.reason });
      toast.success('تم تعديل الرصيد يدوياً بنجاح');
      setAdjustModal({ isOpen: false, balanceType: 'USD', newAmount: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'تعذر تعديل الرصيد');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500 font-bold">جاري التحميل...</div>;
  }

  if (error) {
    return <div className="p-8 text-center space-y-4" role="alert"><p className="font-bold text-red-700">{error}</p><button className="btn-navy" onClick={fetchData}>إعادة المحاولة</button></div>;
  }

  const { balances, stats } = bankData;

  return (
    <div className="space-y-6 fade-up" dir="rtl">
      {/* Header Cards */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h2 className="text-xl font-black flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[var(--c-navy)]" />
          حساب البنك الداخلي
        </h2>
        <div className="flex gap-2">
          <button onClick={() => setAdjustModal({...adjustModal, isOpen: true})} className="btn-navy bg-[var(--c-gold)] text-[var(--c-navy)] flex items-center gap-2">
            <Edit2 className="w-4 h-4" /> تعديل الرصيد يدويًا
          </button>
          <button onClick={handleExportCSV} className="btn-navy flex items-center gap-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border-none">
            <Download className="w-4 h-4" /> تصدير CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm flex flex-col justify-center">
          <div className="flex items-center gap-3 text-[var(--c-muted)] mb-2">
            <DollarSign className="w-5 h-5" />
            <h3 className="font-bold text-sm">الرصيد الكلي (USD)</h3>
          </div>
          <p className="text-2xl font-black text-[var(--c-navy)]">${balances?.USD?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm flex flex-col justify-center">
          <div className="flex items-center gap-3 text-[var(--c-muted)] mb-2">
            <Wallet className="w-5 h-5" />
            <h3 className="font-bold text-sm">الرصيد الكلي (LBP)</h3>
          </div>
          <p className="text-2xl font-black text-[var(--c-navy)]">{balances?.LBP?.toLocaleString() || 0} ل.ل</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm flex flex-col justify-center">
          <div className="flex items-center gap-3 text-[var(--c-muted)] mb-2">
            <ArrowUpFromLine className="w-5 h-5" />
            <h3 className="font-bold text-sm">أرباح اليوم</h3>
          </div>
          <p className="text-2xl font-black text-green-600">${stats?.todayRevenue || 0}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm flex flex-col justify-center">
          <div className="flex items-center gap-3 text-[var(--c-muted)] mb-2">
            <Building2 className="w-5 h-5" />
            <h3 className="font-bold text-sm">أرباح الشهر</h3>
          </div>
          <p className="text-2xl font-black text-[var(--c-navy)]">${stats?.monthlyRevenue || 0}</p>
        </div>
      </div>

      {/* Stats Summary & Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm">
          <h2 className="font-bold text-lg mb-4 text-[var(--c-navy)] border-b pb-2">ملخص الأرباح</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-[var(--c-muted)]">الاشتراكات</span>
              <span className="font-black">${stats?.subscriptionsRevenue || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-[var(--c-muted)]">المتجر</span>
              <span className="font-black">${stats?.marketplaceRevenue || 0}</span>
            </div>
            <div className="pt-4 border-t flex justify-between items-center text-[var(--c-navy)]">
              <span className="font-bold">الإجمالي</span>
              <span className="font-black text-xl">${stats?.totalRevenue || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm flex flex-col">
          <h2 className="font-bold text-lg mb-4 text-[var(--c-navy)] border-b pb-2">اتجاه الأرباح</h2>
          <div className="flex-1 flex items-end gap-2 h-48 mt-4">
            {bankData.revenueTrend.length === 0 ? <div className="w-full text-center text-sm text-[var(--c-muted)] self-center">لا توجد إيرادات مسجلة خلال آخر 7 أيام.</div> : bankData.revenueTrend.map((point, i) => {
              const max = Math.max(...bankData.revenueTrend.map(x => Number(x.amount || 0)), 1);
              const h = Math.max(3, Math.round((Number(point.amount || 0) / max) * 100));
              return <div key={point.date || i} className="flex-1 bg-[var(--c-gold)] rounded-t-md opacity-80 hover:opacity-100 transition-opacity" style={{ height: `${h}%` }} title={`${point.date}: ${point.amount} USD`} />;
            })}
          </div>
        </div>
      </div>

      {/* Transaction Log */}
      <div className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm">
        <h2 className="font-bold text-lg mb-4 text-[var(--c-navy)] border-b pb-2">سجل الحركات</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-slate-50 text-[var(--c-muted)]">
              <tr>
                <th className="px-4 py-3 font-bold rounded-tr-lg">التاريخ</th>
                <th className="px-4 py-3 font-bold">النوع</th>
                <th className="px-4 py-3 font-bold">التصنيف</th>
                <th className="px-4 py-3 font-bold">القيمة</th>
                <th className="px-4 py-3 font-bold">المستخدم (من/إلى)</th>
                <th className="px-4 py-3 font-bold">ملاحظة</th>
                <th className="px-4 py-3 font-bold rounded-tl-lg text-left">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-6 text-gray-500">لا يوجد حركات</td>
                </tr>
              ) : (
                transactions.map((tx, i) => (
                  <tr key={tx.id || i} className="border-b border-gray-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-xs">{new Date(tx.date).toLocaleString('ar-LB')}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                        tx.type === 'deposit' || tx.type === 'revenue' ? 'bg-green-100 text-green-700' : 
                        tx.type === 'withdraw' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{tx.category}</td>
                    <td className="px-4 py-3 font-black text-[var(--c-navy)]" dir="ltr">
                      {tx.currency === 'USD' ? '$' : ''}{tx.amount?.toLocaleString()}{tx.currency === 'LBP' ? ' LBP' : ''}
                    </td>
                    <td className="px-4 py-3 text-xs">{tx.username || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate">{tx.note}</td>
                    <td className="px-4 py-3 text-left">
                      <button onClick={() => handleArchive(tx.id)} className="p-2 text-[var(--c-muted)] hover:text-red-500 transition rounded-lg hover:bg-red-50" title="أرشفة">
                        <Archive className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <form onSubmit={handleDeposit} className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm">
          <h2 className="font-bold text-lg mb-4 text-[var(--c-navy)] flex items-center gap-2"><ArrowDownToLine className="w-5 h-5 text-green-600"/> إيداع في البنك</h2>
          <div className="space-y-3">
            <div>
              <label className="label-pro">العملة</label>
              <select className="input-pro" value={depositForm.currency} onChange={e => setDepositForm({...depositForm, currency: e.target.value})} required>
                <option value="LBP">LBP</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="label-pro">القيمة</label>
              <input type="number" className="input-pro" value={depositForm.amount} onChange={e => setDepositForm({...depositForm, amount: e.target.value})} required />
            </div>
            <div>
              <label className="label-pro">ملاحظة</label>
              <input type="text" className="input-pro" value={depositForm.note} onChange={e => setDepositForm({...depositForm, note: e.target.value})} />
            </div>
            <button type="submit" className="btn-navy w-full mt-2">إيداع</button>
          </div>
        </form>

        <form onSubmit={handleWithdraw} className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm">
          <h2 className="font-bold text-lg mb-4 text-[var(--c-navy)] flex items-center gap-2"><ArrowUpFromLine className="w-5 h-5 text-red-600"/> سحب من البنك</h2>
          <div className="space-y-3">
            <div>
              <label className="label-pro">العملة</label>
              <select className="input-pro" value={withdrawForm.currency} onChange={e => setWithdrawForm({...withdrawForm, currency: e.target.value})} required>
                <option value="LBP">LBP</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="label-pro">القيمة</label>
              <input type="number" className="input-pro" value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})} required />
            </div>
            <div>
              <label className="label-pro">ملاحظة</label>
              <input type="text" className="input-pro" value={withdrawForm.note} onChange={e => setWithdrawForm({...withdrawForm, note: e.target.value})} />
            </div>
            <button type="submit" className="btn-navy w-full mt-2 bg-red-900 hover:bg-red-800">سحب</button>
          </div>
        </form>

        <form onSubmit={handleTransfer} className="bg-white rounded-2xl p-5 border border-[var(--c-border)] shadow-sm">
          <h2 className="font-bold text-lg mb-4 text-[var(--c-navy)] flex items-center gap-2"><Send className="w-5 h-5 text-blue-600"/> تحويل لمستخدم</h2>
          <div className="space-y-3">
            <div>
              <label className="label-pro">المستخدم</label>
              <select className="input-pro" value={transferForm.userId} onChange={e => setTransferForm({...transferForm, userId: e.target.value})} required>
                <option value="">اختر المستخدم</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name} (@{u.username})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-pro">العملة</label>
                <select className="input-pro" value={transferForm.currency} onChange={e => setTransferForm({...transferForm, currency: e.target.value})} required>
                  <option value="LBP">LBP</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div>
                <label className="label-pro">القيمة</label>
                <input type="number" className="input-pro" value={transferForm.amount} onChange={e => setTransferForm({...transferForm, amount: e.target.value})} required />
              </div>
            </div>
            <div>
              <label className="label-pro">ملاحظة</label>
              <input type="text" className="input-pro" value={transferForm.note} onChange={e => setTransferForm({...transferForm, note: e.target.value})} />
            </div>
            <button type="submit" className="btn-gold w-full mt-2">تحويل</button>
          </div>
        </form>
      </div>

      {adjustModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto shadow-2xl p-4 sm:p-6" role="dialog" aria-modal="true">
            <h3 className="text-lg font-black mb-4 flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[var(--c-gold)]" />
              تعديل الرصيد يدوياً
            </h3>
            
            <form onSubmit={handleManualAdjust} className="space-y-4">
              <div>
                <label className="label-pro">نوع الرصيد</label>
                <select 
                  value={adjustModal.balanceType} 
                  onChange={e => setAdjustModal({...adjustModal, balanceType: e.target.value})} 
                  className="input-pro"
                >
                  <option value="USD">USD ($)</option>
                  <option value="LBP">LBP (ل.ل)</option>
                </select>
              </div>

              <div>
                <label className="label-pro">الرصيد الجديد النهائي</label>
                <input 
                  type="number" 
                  value={adjustModal.newAmount} 
                  onChange={e => setAdjustModal({...adjustModal, newAmount: e.target.value})} 
                  className="input-pro text-lg font-bold" 
                  required
                />
              </div>

              <div>
                <label className="label-pro">سبب تعديل الرصيد <span className="text-red-500">*</span></label>
                <textarea 
                  value={adjustModal.reason} 
                  onChange={e => setAdjustModal({...adjustModal, reason: e.target.value})} 
                  className="input-pro" 
                  rows="3" 
                  placeholder="سبب إلزامي للتعديل اليدوي..."
                  required
                ></textarea>
              </div>

              <div className="flex flex-col gap-2 mt-6 sm:flex-row">
                <button type="submit" className="btn-navy flex-1">حفظ التعديل</button>
                <button type="button" onClick={() => setAdjustModal({...adjustModal, isOpen: false})} className="btn-navy bg-slate-200 text-slate-700 flex-1 hover:bg-slate-300">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
